import { Component, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Booking } from '../../../../core/models/entities/booking';
import {
  Payment,
  PaymentStatus,
} from '../../../../core/models/entities/payment';

@Component({
  selector: 'app-customer-payments-table',
  template: `
    <app-table
      [data]="payments()"
      [trackByFn]="trackByPaymentId"
      [clickableRows]="false"
      [emptyStateTitle]="'customer-payments.no-payments' | transloco"
      [emptyStateMessage]="'customer-payments.no-payments-message' | transloco"
      emptyStateIcon="far fa-credit-card"
    >
      <!-- Method Column -->
      <app-table-column
        field="method"
        [header]="'customer-payments.col-method' | transloco"
        [alwaysVisibleOnMobile]="true"
        [showLabelOnMobile]="false"
      >
        <ng-template let-payment>
          <div class="font-medium">
            {{ payment.method_humanized }}
          </div>
        </ng-template>
      </app-table-column>

      <!-- Date Column -->
      <app-table-column field="date" [header]="'customer-payments.col-date' | transloco">
        <ng-template let-payment>
          {{ payment.updated_at | date: 'dd.MM.yyyy HH:mm' }}
        </ng-template>
      </app-table-column>

      <!-- Amount Column -->
      <app-table-column
        field="amount"
        [alwaysVisibleOnMobile]="true"
        [showLabelOnMobile]="false"
        [header]="'customer-payments.col-amount' | transloco"
        headerClasses="justify-end"
      >
        <ng-template let-payment>
          <div class="sm:text-right">
            {{ payment.value | chf }}
          </div>
        </ng-template>
      </app-table-column>

      <!-- Status Column -->
      <app-table-column field="status" [header]="'customer-payments.col-status' | transloco">
        <ng-template let-payment>
          <app-badge
            [variant]="getPaymentStatusBadgeVariant(payment.status)"
            size="sm"
          >
            {{ getPaymentStatusLabel(payment.status) }}
          </app-badge>
        </ng-template>
      </app-table-column>
    </app-table>
  `,
  standalone: false,
})
export class CustomerPaymentsTableComponent {
  private translocoService = inject(TranslocoService);

  booking = input.required<Booking>();
  payments = input<Payment[]>([]);

  PaymentStatus = PaymentStatus;

  trackByPaymentId = (payment: Payment): number => payment.id;

  getPaymentStatusLabel(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Fulfill:
        return this.translocoService.translate('customer-payments.status.paid');
      case PaymentStatus.Pending:
        return this.translocoService.translate('customer-payments.status.pending');
      case PaymentStatus.Authorized:
        return this.translocoService.translate('customer-payments.status.preauthorized');
      case PaymentStatus.Failed:
        return this.translocoService.translate('customer-payments.status.failed');
      default:
        return this.translocoService.translate('customer-payments.status.pending');
    }
  }

  getPaymentStatusBadgeVariant(
    status: PaymentStatus,
  ): 'success' | 'secondary' | 'danger' | 'outlined' {
    switch (status) {
      case PaymentStatus.Fulfill:
        return 'success';
      case PaymentStatus.Pending:
        return 'outlined';
      case PaymentStatus.Authorized:
        return 'outlined';
      case PaymentStatus.Failed:
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
